import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity,
    AlertTriangle,
    BarChart3,
    Car,
    ChevronDown,
    ChevronRight,
    Copy,
    ExternalLink,
    FileText,
    Globe,
    Loader2,
    Package,
    RefreshCw,
    Search,
    ShieldCheck,
    Sparkles,
    Upload,
    Wrench,
    ArrowLeftRight,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { partsouqOem, rayicIstihbarat } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TABS = [
    { id: "komuta", label: "Komuta", icon: BarChart3 },
    { id: "vin", label: "VIN & Dosya", icon: Car },
    { id: "karsilastir", label: "Karşılaştır", icon: ArrowLeftRight },
    { id: "ekspertiz", label: "Ekspertiz", icon: Upload },
];

function tl(n) {
    if (n == null || n === "") return "—";
    return `₺${Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
}

function pct(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(1)}%`;
}

function DurumBadge({ durum, label }) {
    const cls =
        durum === "yuksek"
            ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
            : durum === "uyumlu" || durum === "oem_bulundu"
              ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
              : durum === "dusuk"
                ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                : "border-slate-500/40 text-slate-400 bg-slate-500/10";
    return (
        <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
            {label || durum || "—"}
        </span>
    );
}

function RiskBadge({ risk }) {
    const cls =
        risk === "kritik"
            ? "border-rose-500/50 text-rose-300 bg-rose-500/15"
            : risk === "orta"
              ? "border-amber-500/50 text-amber-300 bg-amber-500/15"
              : "border-emerald-500/50 text-emerald-300 bg-emerald-500/15";
    const label = risk === "kritik" ? "Kritik risk" : risk === "orta" ? "Orta risk" : "Düşük risk";
    return (
        <span className={`inline-flex text-xs px-3 py-1 rounded-full border font-medium ${cls}`}>
            {label}
        </span>
    );
}

function KpiCard({ label, value, hint, accent = "gold" }) {
    const ring =
        accent === "red"
            ? "border-rose-500/30 bg-rose-500/5"
            : accent === "emerald"
              ? "border-emerald-500/30 bg-emerald-500/5"
              : accent === "cyan"
                ? "border-cyan-500/30 bg-cyan-500/5"
                : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl text-white mt-1">{value ?? "—"}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function ParcaSatir({ p, expanded, onToggle }) {
    const oemKaynak = p.partsouq_kaynak || "—";
    return (
        <div className="border border-slate-700/50 rounded-xl overflow-hidden bg-[#0D1324]/60">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-800/30 transition"
            >
                {expanded ? (
                    <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-white font-medium text-sm">{p.parca_adi}</span>
                        <DurumBadge durum={p.durum} label={p.durum_label} />
                        {p.oem_kod ? (
                            <span className="text-[10px] font-mono text-cyan-400/90 bg-cyan-500/10 px-2 py-0.5 rounded">
                                {p.oem_kod}
                            </span>
                        ) : null}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">{p.karsilastirma_mesaj}</div>
                </div>
                <div className="text-right shrink-0 space-y-0.5">
                    <div className="text-xs text-slate-400">Rapor {tl(p.rapor_tutar)}</div>
                    <div className="text-sm text-emerald-300">OEM {tl(p.oem_oneri)}</div>
                    {p.fark_yuzde != null ? (
                        <div
                            className={`text-[10px] ${Number(p.fark_yuzde) > 10 ? "text-rose-400" : "text-slate-500"}`}
                        >
                            {pct(p.fark_yuzde)}
                        </div>
                    ) : null}
                </div>
            </button>
            {expanded ? (
                <div className="px-4 pb-4 pt-0 border-t border-slate-700/40 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                        <div className="bg-slate-800/40 rounded-lg p-2">
                            <div className="text-slate-500">Partsouq OEM</div>
                            <div className="text-cyan-300">{tl(p.partsouq_fiyat)}</div>
                        </div>
                        <div className="bg-slate-800/40 rounded-lg p-2">
                            <div className="text-slate-500">OEM min–max</div>
                            <div className="text-white">
                                {tl(p.oem_min)} – {tl(p.oem_max)}
                            </div>
                        </div>
                        <div className="bg-slate-800/40 rounded-lg p-2">
                            <div className="text-slate-500">Güven</div>
                            <div className="text-white">%{p.guven_skoru ?? "—"}</div>
                        </div>
                        <div className="bg-slate-800/40 rounded-lg p-2">
                            <div className="text-slate-500">Kaynak</div>
                            <div className="text-white capitalize">{oemKaynak.replace(/_/g, " ")}</div>
                        </div>
                    </div>
                    {p.partsouq_url ? (
                        <a
                            href={p.partsouq_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                        >
                            <ExternalLink className="h-3 w-3" /> Partsouq&apos;da aç
                        </a>
                    ) : null}
                    {(p.partsouq_ilanlar || p.tum_oem_ilanlar || []).length > 0 ? (
                        <div className="space-y-1">
                            <div className="text-[10px] uppercase text-slate-500">OEM ilanları</div>
                            {(p.tum_oem_ilanlar || p.partsouq_ilanlar || []).slice(0, 5).map((il, i) => (
                                <div
                                    key={i}
                                    className="flex justify-between text-[11px] bg-slate-900/50 rounded px-2 py-1"
                                >
                                    <span className="text-slate-300 truncate flex-1 mr-2">
                                        {il.baslik || il.oem_kod || "—"}
                                    </span>
                                    <span className="text-emerald-400 shrink-0">{tl(il.fiyat)}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    {p.ai_oem?.aciklama ? (
                        <div className="text-[11px] text-violet-300/80 flex gap-1">
                            <Sparkles className="h-3 w-3 shrink-0 mt-0.5" />
                            AI OEM: {p.ai_oem.aciklama}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export default function PartsouqOemKarsilastirmaPage() {
    const [tab, setTab] = useState("komuta");
    const [ozet, setOzet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [vin, setVin] = useState("");
    const [vinDecode, setVinDecode] = useState(null);
    const [dosyaId, setDosyaId] = useState("");
    const [dosyaSecim, setDosyaSecim] = useState({ dosyalar: [], muvekkiller: [] });
    const [arac, setArac] = useState(null);
    const [sonuc, setSonuc] = useState(null);
    const [expandedIdx, setExpandedIdx] = useState(null);
    const [aiOem, setAiOem] = useState(true);
    const [derin, setDerin] = useState(true);
    const [manuelParcalar, setManuelParcalar] = useState([{ parca_adi: "", tutar: "" }]);
    const [ekspertizFile, setEkspertizFile] = useState(null);
    const [hasarFarki, setHasarFarki] = useState(null);
    const [loadingMesaj, setLoadingMesaj] = useState("");

    const loadOzet = useCallback(async () => {
        try {
            const d = await partsouqOem.ozet();
            setOzet(d);
        } catch {
            toast.error("Özet yüklenemedi");
        }
    }, []);

    const loadDosyaSecim = useCallback(async (q = "") => {
        try {
            const d = await rayicIstihbarat.dosyaSecim({ q, limit: 30 });
            setDosyaSecim(d);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        loadOzet();
        loadDosyaSecim();
    }, [loadOzet, loadDosyaSecim]);

    const decodeVin = async () => {
        if (!vin || vin.length < 17) {
            toast.error("17 haneli VIN girin");
            return;
        }
        setLoading(true);
        try {
            const d = await partsouqOem.vinDecode({ vin });
            setVinDecode(d);
            toast.success(`VIN: ${d.make} ${d.model} ${d.model_year || ""}`);
        } catch (e) {
            toast.error(e?.response?.data?.error || "VIN decode başarısız");
        } finally {
            setLoading(false);
        }
    };

    const loadDosya = async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const d = await partsouqOem.dosya(id);
            setArac(d);
            if (d.vin) setVin(d.vin);
            if (d.vin_decode) setVinDecode(d.vin_decode);
            toast.success(`Dosya yüklendi: ${d.dosya_no}`);
        } catch {
            toast.error("Dosya yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    const karsilastir = async () => {
        setLoading(true);
        setSonuc(null);
        try {
            const payload = {
                vin,
                ai_oem: aiOem ? "1" : "0",
                derin: derin ? "1" : "0",
            };
            if (dosyaId) {
                payload.dosya_id = Number(dosyaId);
            } else {
                const parcalar = manuelParcalar
                    .filter((p) => p.parca_adi?.trim())
                    .map((p) => ({
                        parca_adi: p.parca_adi.trim(),
                        tutar: p.tutar ? Number(p.tutar) : null,
                    }));
                if (!parcalar.length) {
                    toast.error("En az bir parça girin veya dosya seçin");
                    setLoading(false);
                    return;
                }
                payload.parcalar = parcalar;
                payload.marka = vinDecode?.make || arac?.marka || "";
                payload.model = vinDecode?.model || arac?.model || "";
                payload.model_yili = vinDecode?.model_year || arac?.model_yili || 0;
            }
            const d = await partsouqOem.karsilastir(payload);
            setSonuc(d);
            if (d.arac) setArac(d.arac);
            setTab("karsilastir");
            toast.success(`${d.ozet?.parca_sayisi || 0} parça karşılaştırıldı`);
        } catch (e) {
            toast.error(e?.response?.data?.error || e?.response?.data?.detail || "Karşılaştırma başarısız");
        } finally {
            setLoading(false);
        }
    };

    const ekspertizYukle = async () => {
        if (!ekspertizFile) {
            toast.error("Ekspertiz dosyası seçin");
            return;
        }
        const vinKullan = (vin || arac?.vin || "").trim();
        if (vinKullan.length < 17) {
            toast.error("VIN gerekli — dosya seçin (şase) veya VIN & Dosya sekmesinden girin");
            return;
        }
        setLoading(true);
        setLoadingMesaj("Ekspertiz okunuyor, parçalar taranıyor… (1–3 dk sürebilir)");
        setSonuc(null);
        setHasarFarki(null);
        try {
            const fd = new FormData();
            fd.append("file", ekspertizFile);
            fd.append("vin", vinKullan);
            if (dosyaId) fd.append("dosya_id", dosyaId);
            fd.append("ai_oem", aiOem ? "1" : "0");
            fd.append("derin", derin ? "1" : "0");
            const d = await partsouqOem.ekspertizAnaliz(fd);
            if (!d.karsilastirma?.parcalar?.length) {
                toast.error(d.error || "Parça karşılaştırması boş döndü");
                return;
            }
            setSonuc(d.karsilastirma);
            setHasarFarki(d.hasar_farki || null);
            if (d.arac) setArac(d.arac);
            if (d.belge_analiz?.marka) {
                setVinDecode((prev) => ({
                    ...(prev || {}),
                    make: d.belge_analiz.marka,
                    model: d.belge_analiz.model,
                }));
            }
            setTab("karsilastir");
            toast.success(
                `${d.karsilastirma.ozet?.parca_sayisi || 0} parça — OEM fiyat karşılaştırması hazır`
            );
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Ekspertiz analizi başarısız";
            toast.error(msg);
            if (e?.response?.data?.belge_analiz?.parcalar?.length) {
                toast.info(
                    `${e.response.data.belge_analiz.parcalar.length} parça okundu; VIN veya tarama hatası`
                );
            }
        } finally {
            setLoading(false);
            setLoadingMesaj("");
        }
    };

    const copyOzet = () => {
        const hf = hasarFarki?.ozet_metin || sonuc?.hasar_farki?.ozet_metin;
        const lines = [
            "=== Partsouq OEM Karşılaştırma ===",
            `VIN: ${sonuc?.vin || vin}`,
            `Araç: ${sonuc?.arac?.marka || arac?.marka} ${sonuc?.arac?.model || arac?.model}`,
            `Rapor toplam: ${tl(sonuc?.ozet?.toplam_rapor_tutar)}`,
            `OEM toplam: ${tl(sonuc?.ozet?.toplam_oem_oneri)}`,
            `Fark: ${tl(sonuc?.ozet?.toplam_fark_tl)} (${pct(sonuc?.ozet?.toplam_fark_yuzde)})`,
            "",
            ...(sonuc?.parcalar || []).map(
                (p) =>
                    `• ${p.parca_adi}: Rapor ${tl(p.rapor_tutar)} / OEM ${tl(p.oem_oneri)} [${p.durum_label}]`
            ),
            hf ? `\n${hf}` : "",
        ].join("\n");
        navigator.clipboard.writeText(lines);
        toast.success("Özet kopyalandı");
    };

    const parcalar = sonuc?.parcalar || [];
    const ozetK = sonuc?.ozet;

    const riskAccent = useMemo(() => {
        const r = ozetK?.risk_seviyesi;
        if (r === "kritik") return "red";
        if (r === "orta") return "gold";
        return "emerald";
    }, [ozetK]);

    return (
        <div className="min-h-screen bg-[#080C18] text-slate-200 p-4 md:p-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest">
                        <Globe className="h-4 w-4" />
                        Partsouq OEM İstihbarat
                    </div>
                    <h1 className="font-display text-2xl md:text-3xl text-white mt-1">
                        VIN Parça Fiyat Karşılaştırma
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                        VIN ile Partsouq OEM kataloğu, rapor parçaları ve orijinal fiyat bandı — gelişmiş
                        hasar farkı analizi.
                    </p>
                </div>
                <div className="flex gap-2">
                    <a
                        href="https://partsouq.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs border border-cyan-500/30 text-cyan-300 px-3 py-2 rounded-lg hover:bg-cyan-500/10"
                    >
                        <ExternalLink className="h-3 w-3" /> partsouq.com
                    </a>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadOzet}
                        className="border-slate-600 text-slate-300"
                    >
                        <RefreshCw className="h-3 w-3 mr-1" /> Yenile
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-slate-700/50 pb-1">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-xs transition ${
                                tab === t.id
                                    ? "bg-[#121A2F] text-[#D4AF37] border border-b-0 border-[#D4AF37]/30"
                                    : "text-slate-500 hover:text-slate-300"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "komuta" ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard
                            label="VIN kayıtlı dosya"
                            value={ozet?.vin_kayitli_dosya ?? "—"}
                            hint={`Toplam ${ozet?.toplam_dosya ?? "—"} dosya`}
                            accent="cyan"
                        />
                        <KpiCard
                            label="VIN oranı"
                            value={ozet?.vin_oran_yuzde != null ? `%${ozet.vin_oran_yuzde}` : "—"}
                            hint="Şase/VIN dolu dosyalar"
                        />
                        <KpiCard
                            label="Modül"
                            value="Partsouq OEM"
                            hint="VIN + parça karşılaştırma"
                            accent="emerald"
                        />
                        <KpiCard
                            label="Kaynak"
                            value="NHTSA + Partsouq"
                            hint="Çok katmanlı OEM tarama"
                        />
                    </div>
                    <div className="rounded-xl border border-slate-700/50 bg-[#0D1324]/60 p-4">
                        <div className="text-xs uppercase text-slate-500 mb-3">Popüler markalar (VIN kayıtlı)</div>
                        <div className="flex flex-wrap gap-2">
                            {(ozet?.populer_markalar || []).map((m, i) => (
                                <span
                                    key={i}
                                    className="text-xs px-2 py-1 rounded-full border border-slate-600 text-slate-300"
                                >
                                    {m.marka} ({m.adet})
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3 text-sm">
                        {[
                            {
                                icon: Car,
                                title: "1. VIN gir / dosya seç",
                                desc: "NHTSA decode + dosya şase alanı",
                            },
                            {
                                icon: Wrench,
                                title: "2. Parçaları tara",
                                desc: "Partsouq OEM + piyasa orijinal fiyat",
                            },
                            {
                                icon: ShieldCheck,
                                title: "3. Rapor karşılaştır",
                                desc: "Hasar farkı risk skoru ve detay",
                            },
                        ].map((s, i) => (
                            <div
                                key={i}
                                className="rounded-xl border border-[#D4AF37]/15 bg-[#121A2F]/50 p-4"
                            >
                                <s.icon className="h-5 w-5 text-[#D4AF37] mb-2" />
                                <div className="text-white font-medium">{s.title}</div>
                                <div className="text-slate-500 text-xs mt-1">{s.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {tab === "vin" ? (
                <div className="grid lg:grid-cols-2 gap-6">
                    <div className="space-y-4 rounded-xl border border-slate-700/50 bg-[#0D1324]/60 p-4">
                        <div className="text-sm font-medium text-white flex items-center gap-2">
                            <Search className="h-4 w-4 text-[#D4AF37]" /> VIN / Şase
                        </div>
                        <Input
                            value={vin}
                            onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17))}
                            placeholder="17 haneli VIN (şase)"
                            className="bg-slate-900/50 border-slate-600 font-mono tracking-wider"
                        />
                        <div className="flex gap-2">
                            <Button onClick={decodeVin} disabled={loading} className="bg-[#D4AF37] text-black hover:bg-[#c9a430]">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Car className="h-4 w-4 mr-1" />}
                                VIN Decode
                            </Button>
                            {vinDecode?.partsouq_vin_url ? (
                                <a
                                    href={vinDecode.partsouq_vin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-xs text-cyan-400 px-3 py-2 border border-cyan-500/30 rounded-md"
                                >
                                    <ExternalLink className="h-3 w-3 mr-1" /> Partsouq katalog
                                </a>
                            ) : null}
                        </div>
                        {vinDecode?.success ? (
                            <div className="rounded-lg bg-slate-800/40 p-3 text-sm space-y-1">
                                <div className="text-white font-medium">
                                    {vinDecode.make} {vinDecode.model} {vinDecode.model_year}
                                </div>
                                <div className="text-slate-400 text-xs">
                                    {vinDecode.manufacturer} · {vinDecode.plant_country}
                                </div>
                                {vinDecode.nhtsa_uyari ? (
                                    <div className="text-amber-400 text-xs">{vinDecode.nhtsa_uyari}</div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    <div className="space-y-4 rounded-xl border border-slate-700/50 bg-[#0D1324]/60 p-4">
                        <div className="text-sm font-medium text-white flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[#D4AF37]" /> Sigorta dosyası
                        </div>
                        <select
                            value={dosyaId}
                            onChange={(e) => {
                                setDosyaId(e.target.value);
                                if (e.target.value) loadDosya(e.target.value);
                            }}
                            className="w-full bg-slate-900/50 border border-slate-600 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="">Dosya seçin…</option>
                            {(dosyaSecim.dosyalar || []).map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                        {arac ? (
                            <div className="rounded-lg bg-slate-800/40 p-3 text-sm">
                                <div className="text-white">
                                    {arac.dosya_no} · {arac.plaka}
                                </div>
                                <div className="text-slate-400 text-xs mt-1">
                                    {arac.marka} {arac.model} {arac.model_yili} · {arac.parca_sayisi} parça
                                </div>
                                {arac.sase ? (
                                    <div className="text-cyan-400/80 text-xs font-mono mt-1">Şase: {arac.sase}</div>
                                ) : (
                                    <div className="text-amber-400 text-xs mt-1">Dosyada şase kaydı yok — VIN girin</div>
                                )}
                                <Link
                                    to={arac.dosya_link}
                                    className="text-xs text-[#D4AF37] hover:underline mt-2 inline-block"
                                >
                                    Dosyayı aç →
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {tab === "karsilastir" ? (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-3 items-center">
                        <label className="flex items-center gap-2 text-xs text-slate-400">
                            <input type="checkbox" checked={aiOem} onChange={(e) => setAiOem(e.target.checked)} />
                            AI OEM kod
                        </label>
                        <label className="flex items-center gap-2 text-xs text-slate-400">
                            <input type="checkbox" checked={derin} onChange={(e) => setDerin(e.target.checked)} />
                            Derin tarama
                        </label>
                        <Button
                            onClick={karsilastir}
                            disabled={loading}
                            className="bg-[#D4AF37] text-black hover:bg-[#c9a430] ml-auto"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                                <Activity className="h-4 w-4 mr-1" />
                            )}
                            Karşılaştırmayı başlat
                        </Button>
                    </div>

                    {!dosyaId ? (
                        <div className="rounded-xl border border-slate-700/50 bg-[#0D1324]/60 p-4 space-y-2">
                            <div className="text-xs text-slate-500 uppercase">Manuel parça listesi</div>
                            {manuelParcalar.map((p, i) => (
                                <div key={i} className="flex gap-2">
                                    <Input
                                        placeholder="Parça adı"
                                        value={p.parca_adi}
                                        onChange={(e) => {
                                            const n = [...manuelParcalar];
                                            n[i] = { ...n[i], parca_adi: e.target.value };
                                            setManuelParcalar(n);
                                        }}
                                        className="flex-1 bg-slate-900/50 border-slate-600"
                                    />
                                    <Input
                                        placeholder="Rapor ₺"
                                        type="number"
                                        value={p.tutar}
                                        onChange={(e) => {
                                            const n = [...manuelParcalar];
                                            n[i] = { ...n[i], tutar: e.target.value };
                                            setManuelParcalar(n);
                                        }}
                                        className="w-28 bg-slate-900/50 border-slate-600"
                                    />
                                </div>
                            ))}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setManuelParcalar([...manuelParcalar, { parca_adi: "", tutar: "" }])}
                                className="text-slate-400"
                            >
                                + Parça ekle
                            </Button>
                        </div>
                    ) : null}

                    {hasarFarki?.ozet_metin ? (
                        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-slate-300 whitespace-pre-wrap">
                            {hasarFarki.ozet_metin}
                        </div>
                    ) : null}

                    {ozetK ? (
                        <>
                            <div className="flex flex-wrap items-center gap-3">
                                <RiskBadge risk={ozetK.risk_seviyesi} />
                                {sonuc?.partsouq_vin_url ? (
                                    <a
                                        href={sonuc.partsouq_vin_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-cyan-400 flex items-center gap-1"
                                    >
                                        <ExternalLink className="h-3 w-3" /> VIN katalog
                                    </a>
                                ) : null}
                                <Button variant="ghost" size="sm" onClick={copyOzet} className="text-slate-400 ml-auto">
                                    <Copy className="h-3 w-3 mr-1" /> Özet kopyala
                                </Button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <KpiCard label="Parça" value={ozetK.parca_sayisi} />
                                <KpiCard label="Rapor toplam" value={tl(ozetK.toplam_rapor_tutar)} accent="gold" />
                                <KpiCard label="OEM toplam" value={tl(ozetK.toplam_oem_oneri)} accent="emerald" />
                                <KpiCard
                                    label="Fark"
                                    value={tl(ozetK.toplam_fark_tl)}
                                    hint={pct(ozetK.toplam_fark_yuzde)}
                                    accent={riskAccent}
                                />
                                <KpiCard
                                    label="Yüksek sapma"
                                    value={ozetK.yuksek_sapma_parca}
                                    hint={`${ozetK.partsouq_bulunan_parca || 0} Partsouq`}
                                    accent={ozetK.yuksek_sapma_parca > 0 ? "red" : "emerald"}
                                />
                            </div>
                        </>
                    ) : null}

                    <div className="space-y-2">
                        {parcalar.map((p, i) => (
                            <ParcaSatir
                                key={i}
                                p={p}
                                expanded={expandedIdx === i}
                                onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
                            />
                        ))}
                        {!parcalar.length ? (
                            <div className="text-center text-slate-500 py-12 border border-dashed border-slate-700 rounded-xl">
                                <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                                VIN + dosya veya parça listesi ile karşılaştırma başlatın
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {loading && loadingMesaj ? (
                <div className="flex items-center gap-3 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 text-sm text-cyan-200">
                    <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                    {loadingMesaj}
                </div>
            ) : null}

            {tab === "ekspertiz" ? (
                <div className="max-w-xl space-y-4 rounded-xl border border-slate-700/50 bg-[#0D1324]/60 p-6">
                    <div className="text-sm font-medium text-white flex items-center gap-2">
                        <Upload className="h-4 w-4 text-[#D4AF37]" /> Ekspertiz raporu yükle
                    </div>
                    <p className="text-xs text-slate-500">
                        PDF/görsel ekspertizden parçalar çıkarılır, her biri OEM fiyatı ile karşılaştırılır.
                        Önce dosya seçerseniz şase otomatik kullanılır. İşlem 1–3 dakika sürebilir.
                    </p>
                    {dosyaId && arac?.sase ? (
                        <div className="text-xs text-emerald-400/90 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                            Dosya şasesi: <span className="font-mono">{arac.sase}</span>
                        </div>
                    ) : null}
                    <Input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(e) => setEkspertizFile(e.target.files?.[0] || null)}
                        className="bg-slate-900/50 border-slate-600"
                    />
                    <Input
                        value={vin}
                        onChange={(e) => setVin(e.target.value.toUpperCase().slice(0, 17))}
                        placeholder="VIN (zorunlu)"
                        className="bg-slate-900/50 border-slate-600 font-mono"
                    />
                    <Button
                        onClick={ekspertizYukle}
                        disabled={loading || !ekspertizFile}
                        className="w-full bg-[#D4AF37] text-black hover:bg-[#c9a430]"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                        Ekspertiz + Partsouq analiz
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
